import React, { useState, useRef, useEffect } from 'react';
import './EventRecap.css';
import useScrollAnimation from '../hooks/useScrollAnimation';

// Fotos del dentsu Day 2026, redimensionadas a 1920px y en webp.
const TOTAL_FOTOS = 57;
const FOTOS_VISIBLES = 30;

// Portada de la galeria: foto grupal del equipo en el escenario (original _A746372.jpg).
const FOTO_PORTADA = '/images/event-recap/2026/dentsu-day-2026-portada.webp';

const todasLasFotos = Array.from({ length: TOTAL_FOTOS }, (_, i) =>
  `/images/event-recap/2026/dentsu-day-2026-${String(i + 1).padStart(3, '0')}.webp`
);

// Fisher-Yates shuffle algorithm to randomize array
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const EventRecap = () => {
  const [titleRef, titleVisible] = useScrollAnimation(0.3);
  const [contentRef, contentVisible] = useScrollAnimation(0.3);
  const [galleryRef, galleryVisible] = useScrollAnimation(0.3);

  // El sorteo se hace una sola vez por visita: si se rehace en cada render,
  // las fotos cambian mientras la galeria se desplaza.
  // La portada va siempre primero para que las flechas recorran la misma
  // secuencia que la tira de miniaturas.
  const [allPhotos] = useState(() => [
    FOTO_PORTADA,
    ...shuffleArray(todasLasFotos).slice(0, FOTOS_VISIBLES),
  ]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const galleryScrollRef = useRef(null);
  const isPausedRef = useRef(false);
  // Apenas la persona navega la galeria se corta el auto-scroll: si la tira
  // se sigue moviendo, cuesta acertarle a la miniatura que se quiere.
  const userInteractedRef = useRef(false);
  const touchStartXRef = useRef(null);

  const total = allPhotos.length;

  const goTo = (index) => {
    userInteractedRef.current = true;
    setCurrentIndex(index);
  };

  const goPrev = () => {
    userInteractedRef.current = true;
    setCurrentIndex((i) => (i - 1 + total) % total);
  };

  const goNext = () => {
    userInteractedRef.current = true;
    setCurrentIndex((i) => (i + 1) % total);
  };

  const handleViewerKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    }
  };

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartXRef.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;
    if (Math.abs(deltaX) < 40) return;
    if (deltaX > 0) goPrev();
    else goNext();
  };

  // Centra la miniatura activa en la tira. Se usa scrollTo sobre la tira y no
  // scrollIntoView, que tambien moveria la pagina en vertical.
  useEffect(() => {
    if (!userInteractedRef.current) return;
    const gallery = galleryScrollRef.current;
    const item = gallery?.children[currentIndex];
    if (!item) return;
    gallery.scrollTo({
      left: item.offsetLeft - (gallery.clientWidth - item.offsetWidth) / 2,
      behavior: 'smooth'
    });
  }, [currentIndex]);

  // Precarga la anterior y la siguiente para que el cambio con flechas sea inmediato.
  useEffect(() => {
    [currentIndex - 1, currentIndex + 1].forEach((i) => {
      const img = new Image();
      img.src = allPhotos[(i + total) % total];
    });
  }, [currentIndex, allPhotos, total]);

  const numberOfDots = 8; // Number of navigation dots

  const handleDotClick = (index) => {
    const gallery = galleryScrollRef.current;
    if (!gallery) return;

    userInteractedRef.current = true;

    const maxScroll = gallery.scrollWidth - gallery.clientWidth;

    // Calculate scroll position (divide total scroll into equal sections)
    const scrollPosition = (index / (numberOfDots - 1)) * maxScroll;

    gallery.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  };

  // Continuous auto-scroll effect for gallery
  useEffect(() => {
    const gallery = galleryScrollRef.current;
    if (!gallery) return;

    let animationFrameId;
    const scrollSpeed = 0.5; // Pixels per frame (adjust for faster/slower)
    let hasReachedEnd = false;

    const animate = () => {
      if (!isPausedRef.current && !userInteractedRef.current && !hasReachedEnd && gallery) {
        const maxScroll = gallery.scrollWidth - gallery.clientWidth;
        const currentScroll = gallery.scrollLeft;

        if (currentScroll >= maxScroll - 5) {
          // Stop scrolling when reaching the end (with 5px buffer)
          hasReachedEnd = true;
          // Reset to beginning after a pause
          setTimeout(() => {
            gallery.scrollTo({ left: 0, behavior: 'smooth' });
            setTimeout(() => {
              hasReachedEnd = false;
            }, 1000);
          }, 2000);
        } else {
          // Continuous scroll forward
          gallery.scrollLeft += scrollSpeed;
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    // Update current slide indicator based on scroll position
    const handleScroll = () => {
      const scrollLeft = gallery.scrollLeft;
      const maxScroll = gallery.scrollWidth - gallery.clientWidth;

      // Calculate which dot should be active based on scroll position
      // Divide the scroll into equal sections for each dot
      const scrollProgress = scrollLeft / maxScroll; // 0 to 1
      const currentDot = Math.round(scrollProgress * (numberOfDots - 1));
      setCurrentSlide(currentDot);
    };

    // Pause auto-scroll on hover
    const handleMouseEnter = () => {
      isPausedRef.current = true;
    };

    const handleMouseLeave = () => {
      isPausedRef.current = false;
    };

    gallery.addEventListener('mouseenter', handleMouseEnter);
    gallery.addEventListener('mouseleave', handleMouseLeave);
    gallery.addEventListener('scroll', handleScroll);

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
      gallery.removeEventListener('mouseenter', handleMouseEnter);
      gallery.removeEventListener('mouseleave', handleMouseLeave);
      gallery.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section className="event-recap">
      <div className="event-recap-container">
        {/* Left Content */}
        <div className="event-recap-left">
          <h2
            ref={titleRef}
            className={`event-recap-title ${titleVisible ? 'fade-in-up' : 'animate-on-scroll'}`}
          >
            Cómo vivimos nuestro<br />
            <strong>dentsu Day</strong>
          </h2>

          <div
            ref={contentRef}
            className={`event-recap-content ${contentVisible ? 'fade-in-up' : 'animate-on-scroll'}`}
          >
            <p>
              Una mañana para conversar sobre retail media, búsqueda generativa, la nueva Consumer Vision y medición con datos propios, junto a líderes, clientes y aliados de la industria.
            </p>
            <p>
              Gracias a speakers, sponsors y asistentes. Nos reencontramos en el <strong>dentsu Day 2027</strong>.
            </p>
          </div>
        </div>

        {/* Right Image Element */}
        <div
          className="event-recap-right"
          tabIndex={0}
          role="region"
          aria-roledescription="carrusel"
          aria-label="Galería dentsu Day 2026"
          onKeyDown={handleViewerKeyDown}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <img
            src={allPhotos[currentIndex]}
            alt={`dentsu Day 2026 - Foto ${currentIndex + 1} de ${allPhotos.length}`}
            className="event-recap-image"
          />
          <button
            className="viewer-arrow viewer-arrow-prev"
            onClick={goPrev}
            aria-label="Foto anterior"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
            </svg>
          </button>
          <button
            className="viewer-arrow viewer-arrow-next"
            onClick={goNext}
            aria-label="Foto siguiente"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z" fill="currentColor"/>
            </svg>
          </button>
          <span className="viewer-counter" aria-live="polite">
            {currentIndex + 1} / {allPhotos.length}
          </span>
        </div>
      </div>

      {/* Photo Gallery */}
      <div
        ref={(node) => {
          // Assign to animation ref
          if (galleryRef && 'current' in galleryRef) {
            galleryRef.current = node;
          }
          // Assign to scroll ref
          galleryScrollRef.current = node;
        }}
        className={`event-gallery ${galleryVisible ? 'fade-in-up' : 'animate-on-scroll'}`}
      >
        {allPhotos.map((photo, index) => (
          <div
            key={index}
            className={`gallery-item ${currentIndex === index ? 'active' : ''}`}
            onClick={() => goTo(index)}
          >
            <img
              src={photo}
              alt={`dentsu Day 2026 - Momento ${index + 1}`}
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Gallery Indicators */}
      <div className="gallery-indicators">
        {Array.from({ length: numberOfDots }).map((_, index) => (
          <span
            key={index}
            className={`indicator-dot ${currentSlide === index ? 'active' : ''}`}
            onClick={() => handleDotClick(index)}
            aria-label={`Ir a sección ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default EventRecap;
